import { useState, useEffect } from 'react';
import { UserPlus, Users, Pencil, Key, Trash2, Loader2, Search, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface StaffMember {
  id: number;
  username: string;
  full_name: string;
  role: string;
  created_at: string;
  login_id: number;
}

// Define the type for the joined data structure
interface StaffJoinResult {
  id: number;
  full_name: string;
  created_at: string;
  login_id: number;
  logins: {
    username: string;
    role: string;
  };
}

interface StaffCredentials {
  username: string;
  password: string;
  full_name: string;
  role: string;
}

export default function Staff() {
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [newCredentials, setNewCredentials] = useState<StaffCredentials>({
    username: '',
    password: '',
    full_name: '',
    role: 'sales_assistant'
  });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    fetchStaffMembers();
  }, []);

  // Debounced username availability check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newCredentials.username && showAddModal) {
        checkUsernameAvailability(newCredentials.username);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newCredentials.username, showAddModal]);

  const checkUsernameAvailability = async (username: string) => {
    if (!username.trim()) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const { data, error } = await supabase.rpc('is_username_available', {
        p_username: username
      });
      
      if (error) throw error;
      setUsernameAvailable(data);
    } catch (err) {
      console.error('Error checking username availability:', err);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('staff')
        .select(`
          id, 
          full_name, 
          created_at, 
          login_id,
          logins!staff_login_id_fkey (
            username,
            role
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform the data with proper typing
      const transformedData = (data || []).map(staff => {
        // Handle the case where logins is returned as an array by Supabase
        const loginData = Array.isArray(staff.logins) ? staff.logins[0] : staff.logins;
        
        return {
          id: staff.id,
          username: loginData?.username || '',
          full_name: staff.full_name,
          role: loginData?.role || '',
          created_at: staff.created_at,
          login_id: staff.login_id
        };
      });
      
      setStaffMembers(transformedData);
    } catch (err) {
      console.error('Error fetching staff members:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Validate inputs
      if (!newCredentials.username || !newCredentials.password || !newCredentials.full_name) {
        setError('Please fill in all required fields');
        return;
      }

      // Create login first
      const { data: loginData, error: loginError } = await supabase
        .rpc('create_login', {
          p_username: newCredentials.username,
          p_password: newCredentials.password,
          p_role: newCredentials.role
        });

      if (loginError) throw loginError;
      
      // Then create staff with reference to the login
      const { error: staffError } = await supabase
        .from('staff')
        .insert([{
          full_name: newCredentials.full_name,
          login_id: loginData.id
        }]);

      if (staffError) throw staffError;

      // Reset form and refresh data
      setNewCredentials({
        username: '',
        password: '',
        full_name: '',
        role: 'sales_assistant'
      });
      setShowAddModal(false);
      setSuccess('Staff member added successfully');
      fetchStaffMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to add staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStaff = async () => {
    if (!selectedStaff) return;
    
    try {
      setSaving(true);
      setError('');
      
      console.log("Updating staff with:", {
        staffId: selectedStaff.id,
        loginId: selectedStaff.login_id,
        fullName: newCredentials.full_name,
        role: newCredentials.role
      });
      
      // Use the direct update functions that bypass all triggers
      const { data: nameResult, error: nameError } = await supabase.rpc(
        'direct_update_staff_name',
        {
          p_staff_id: selectedStaff.id,
          p_full_name: newCredentials.full_name
        }
      );
      
      console.log("Name direct update result:", { data: nameResult, error: nameError });
      
      if (nameError) {
        console.error("Name update error:", nameError);
        throw new Error(`Failed to update name: ${nameError.message}`);
      }
      
      const { data: roleResult, error: roleError } = await supabase.rpc(
        'direct_update_login_role',
        {
          p_login_id: selectedStaff.login_id,
          p_role: newCredentials.role
        }
      );
      
      console.log("Role direct update result:", { data: roleResult, error: roleError });
      
      if (roleError) {
        console.error("Role update error:", roleError);
        throw new Error(`Failed to update role: ${roleError.message}`);
      }
      
      // Success!
      setShowEditModal(false);
      setSuccess('Staff member updated successfully');
      await fetchStaffMembers();
    } catch (err: any) {
      console.error('Staff update error:', err);
      setError(err.message || 'Failed to update staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedStaff || !newPassword) return;
    
    setSaving(true);
    try {
      // Use the RPC function to update staff password
      const { data, error } = await supabase.rpc('update_staff_password', {
        p_login_id: selectedStaff.login_id,
        p_new_password: newPassword
      });
      
      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Failed to update staff password');
      }

      console.log('Staff password reset successful');
      
      setShowPasswordModal(false);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) {
      console.error('Error resetting password:', err);
      alert(`Failed to reset password: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStaff = async () => {
    if (!selectedStaff) return;
    
    try {
      setSaving(true);
      setError('');
      
      // Delete login record - this will cascade and delete the staff record automatically
      const { error: loginError } = await supabase
        .from('logins')
        .delete()
        .eq('id', selectedStaff.login_id);

      if (loginError) throw loginError;

      setShowDeleteModal(false);
      setSuccess('Staff member deleted successfully');
      fetchStaffMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to delete staff member');
    } finally {
      setSaving(false);
    }
  };

  const filteredStaff = staffMembers.filter(staff => 
    staff.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openEditModal = (staff: StaffMember) => {
    setSelectedStaff(staff);
    setNewCredentials({
      username: staff.username,
      password: '',
      full_name: staff.full_name,
      role: staff.role
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (staff: StaffMember) => {
    setSelectedStaff(staff);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const openDeleteModal = (staff: StaffMember) => {
    setSelectedStaff(staff);
    setShowDeleteModal(true);
  };

  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/20 text-red-300';
      case 'store_manager':
        return 'bg-purple-500/20 text-purple-300';
      case 'assistant_manager':
        return 'bg-blue-500/20 text-blue-300';
      case 'sales_assistant':
        return 'bg-green-500/20 text-green-300';
      case 'invoices_clerk':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'stock_control_manager':
        return 'bg-orange-500/20 text-orange-300';
      case 'warehouse_assistant':
        return 'bg-teal-500/20 text-teal-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      'admin': 'Administrator',
      'sales_assistant': 'Sales Assistant',
      'invoices_clerk': 'Invoices Clerk',
      'store_manager': 'Store Manager',
      'assistant_manager': 'Assistant Manager',
      'stock_control_manager': 'Stock Control Manager',
      'warehouse_assistant': 'Warehouse Assistant'
    };

    return roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto">
      <div className="p-6">
        {/* Header Bar */}
        <div className="relative z-[50] flex items-center justify-between mb-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-500/10 rounded-lg p-2">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Staff Management</h1>
              <p className="text-sm text-white/50">
                Manage staff accounts and permissions
              </p>
            </div>
          </div>
          
          {/* Add Staff Button */}
          <button
            onClick={() => {
              setNewCredentials({
                username: '',
                password: '',
                full_name: '',
                role: 'sales_assistant'
              });
              setUsernameAvailable(null);
              setCheckingUsername(false);
              setError('');
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Staff</span>
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span>{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-300/70 hover:text-green-300">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError('')} className="text-red-300/70 hover:text-red-300">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
            <input
              type="text"
              placeholder="Search staff by name, username, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            />
          </div>
        </div>

        {/* Staff Table */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-lg font-semibold text-white">Staff Members</h2>
          </div>
          
          {filteredStaff.length === 0 ? (
            <div className="p-6 text-center text-white/50">
              {searchQuery ? 'No staff members found matching your search.' : 'No staff members found.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Username</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredStaff.map(staff => (
                    <tr key={staff.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-white">{staff.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-white/70">{staff.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(staff.role)}`}>
                          {getRoleDisplayName(staff.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-white/70">
                        {new Date(staff.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditModal(staff)}
                            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            title="Edit Staff"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openPasswordModal(staff)}
                            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(staff)}
                            className="p-2 text-white/70 hover:text-red-400 hover:bg-white/10 rounded-lg transition-colors"
                            title="Delete Staff"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">Add New Staff Member</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Full Name</label>
                <input
                  type="text"
                  value={newCredentials.full_name}
                  onChange={(e) => setNewCredentials({...newCredentials, full_name: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Username</label>
                <div className="relative">
                  <input
                    type="text"
                    value={newCredentials.username}
                    onChange={(e) => {
                      setNewCredentials({...newCredentials, username: e.target.value});
                      setUsernameAvailable(null);
                    }}
                    className={`w-full px-3 py-2 bg-white/5 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                      usernameAvailable === false ? 'border-red-500/50' : 
                      usernameAvailable === true ? 'border-green-500/50' : 'border-white/20'
                    }`}
                    placeholder="johnsmith"
                  />
                  {checkingUsername && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-4 h-4 text-white/50 animate-spin" />
                    </div>
                  )}
                  {!checkingUsername && usernameAvailable === true && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                  )}
                  {!checkingUsername && usernameAvailable === false && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <X className="w-4 h-4 text-red-400" />
                    </div>
                  )}
                </div>
                {usernameAvailable === false && (
                  <p className="mt-1 text-xs text-red-400">Username is already taken</p>
                )}
                {usernameAvailable === true && (
                  <p className="mt-1 text-xs text-green-400">Username is available</p>
                )}
              </div>
              
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={newCredentials.password}
                  onChange={(e) => setNewCredentials({...newCredentials, password: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Role</label>
                <select
                  value={newCredentials.role}
                  onChange={(e) => setNewCredentials({...newCredentials, role: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                >
                  <option value="sales_assistant">Sales Assistant</option>
                  <option value="admin">Administrator</option>
                  <option value="invoices_clerk">Invoices Clerk</option>
                  <option value="store_manager">Store Manager</option>
                  <option value="assistant_manager">Assistant Manager</option>
                  <option value="stock_control_manager">Stock Control Manager</option>
                  <option value="warehouse_assistant">Warehouse Assistant</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStaff}
                disabled={saving || usernameAvailable === false || !usernameAvailable}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                <span>{saving ? 'Creating...' : 'Create Staff'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">Edit Staff Member</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Full Name</label>
                <input
                  type="text"
                  value={newCredentials.full_name}
                  onChange={(e) => setNewCredentials({...newCredentials, full_name: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Username</label>
                <input
                  type="text"
                  value={newCredentials.username}
                  disabled
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white/50 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-white/50">Username cannot be changed</p>
              </div>
              
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Role</label>
                <select
                  value={newCredentials.role}
                  onChange={(e) => setNewCredentials({...newCredentials, role: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                >
                  <option value="sales_assistant">Sales Assistant</option>
                  <option value="admin">Administrator</option>
                  <option value="invoices_clerk">Invoices Clerk</option>
                  <option value="store_manager">Store Manager</option>
                  <option value="assistant_manager">Assistant Manager</option>
                  <option value="stock_control_manager">Stock Control Manager</option>
                  <option value="warehouse_assistant">Warehouse Assistant</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditStaff}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedStaff && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">Reset Password</h2>
            <p className="text-white/70 mb-4">Enter a new password for <span className="text-white">{selectedStaff.full_name}</span></p>
            
            <div>
              <label className="block text-white/70 text-sm font-medium mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                placeholder="••••••••"
              />
            </div>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={saving || !newPassword}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                <span>{saving ? 'Resetting...' : 'Reset Password'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedStaff && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">Confirm Deletion</h2>
            <p className="text-white/70 mb-4">
              Are you sure you want to delete <span className="text-white">{selectedStaff.full_name}</span>? 
              This action cannot be undone.
            </p>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStaff}
                disabled={saving}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                <span>{saving ? 'Deleting...' : 'Delete Staff'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 